/**
 * Rewarded-ad provider seam. Video offers (watch N ads → Stacks) call this
 * interface; the offer popup is provider-agnostic.
 *
 * AdMob is SCAFFOLDED, not yet live. Rewarded video won't ship in the first
 * release, but the @capacitor-community/admob NATIVE plugin must be in the
 * first binary we send to Apple — native plugins can't be added through a CDN
 * hot-update, only a JS toggle can. So the plugin is installed + synced into the
 * iOS/Android projects now, and we flip `ADS_ENABLED` (a JS-only change, shippable
 * over CDN) when the real AdMob account + ad-unit IDs exist.
 *
 * Until then `createAdProvider()` returns the dev stub, so video offers still
 * work end-to-end in testing without showing a real ad.
 */

import { Capacitor } from '@capacitor/core';
import { AdMob } from '@capacitor-community/admob';

export interface AdProvider {
  /** Show one rewarded ad. Resolves true if watched to completion (reward earned). */
  watchRewarded(): Promise<boolean>;
}

/**
 * Master switch for rewarded ads. FALSE for the first release. Flipping to true
 * is a JS-only change (CDN hot-update) — provided the native plugin is already
 * in the installed binary and real ad-unit IDs are set below.
 */
export const ADS_ENABLED = false;

/**
 * Real Gritplay AdMob rewarded ad-unit IDs (Street Dice Legends). The matching
 * app IDs are baked into the native config (AndroidManifest APPLICATION_ID +
 * iOS Info.plist GADApplicationIdentifier). `createAdProvider()` passes
 * isTesting=true so test ads serve until you're approved + ready for real
 * revenue — flip that to false (a JS/CDN change) to go live.
 */
const REWARDED_UNIT = {
  android: 'ca-app-pub-2772557006800670/7465936362',
  ios: 'ca-app-pub-2772557006800670/4013776512',
};

/** Dev/browser stub — "watches" an ad by waiting briefly, always rewards. */
export class DevStubAdProvider implements AdProvider {
  constructor(private readonly delayMs = 900) {}
  watchRewarded(): Promise<boolean> {
    return new Promise((resolve) => setTimeout(() => resolve(true), this.delayMs));
  }
}

/**
 * Real AdMob rewarded provider. Native-only (web returns false). `isTesting`
 * forces test-ad behaviour even with real unit IDs — keep it on until the app
 * is approved for live ads.
 */
export class AdMobAdProvider implements AdProvider {
  private initialized = false;
  constructor(private readonly adUnitId: string, private readonly isTesting = true) {}

  private async ensureInit(): Promise<void> {
    if (this.initialized) return;
    await AdMob.initialize();
    this.initialized = true;
  }

  async watchRewarded(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      await this.ensureInit();
      await AdMob.prepareRewardVideoAd({ adId: this.adUnitId, isTesting: this.isTesting });
      // Resolves with the reward item ONLY when the user earns the reward;
      // a dismiss-without-reward rejects → caught → false.
      const reward = await AdMob.showRewardVideoAd();
      return !!reward;
    } catch (err) {
      console.warn('[admob] rewarded ad failed:', (err as Error)?.message);
      return false;
    }
  }
}

/**
 * Pick the active provider. Stub unless ads are enabled AND we're on native with
 * a unit ID for this platform. Swapping providers is the only change needed when
 * rewarded video goes live (plus flipping ADS_ENABLED).
 */
export function createAdProvider(): AdProvider {
  if (ADS_ENABLED && Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    const unit = platform === 'ios' ? REWARDED_UNIT.ios
      : platform === 'android' ? REWARDED_UNIT.android
      : null;
    if (unit) return new AdMobAdProvider(unit, true);
  }
  return new DevStubAdProvider();
}
